import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Criticality } from '@uigy/generated/graphql';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { VprOptions } from '@uigy/shared/plugins-grid/enums/vpr.enum';
import { ProgressBarType } from '@uigy/shared/components/ui-progress-bar/progress-bar.service';
import { UiProgressBarComponent } from '@uigy/shared/components/ui-progress-bar/ui-progress-bar.component';
import { UiStackedProgressBarComponent } from '@uigy/shared/components/ui-progress-bar/ui-stacked-progress-bar.component';
import { UiStackedProgressBarSegment } from '@uigy/shared/components/ui-progress-bar/ui-stacked-progress-bar.interfaces';
import { IconComponent } from '@uigy/shared/components/icons/icon/icon.component';
import { MatTooltip } from '@angular/material/tooltip';
import { LanguageService } from '@uigy/shared/services/language.service';

@Component({
  selector: 'app-kpi-drawer',
  imports: [
    UiProgressBarComponent,
    UiStackedProgressBarComponent,
    IconComponent,
    MatTooltip,
  ],
  templateUrl: './kpi-drawer.component.html',
  styleUrl: './kpi-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KpiDrawerComponent {
  private languageService = inject(LanguageService);

  // Icon
  public icon = input<IconsEnum>(IconsEnum.vulnerability);

  // Label
  public label = input.required<string>();
  public infoTooltip = input<string>();

  // Values
  public value = input<number>(0);
  public subValue = input<string>();

  // Progress bar configuration
  public progressBarType = input<ProgressBarType>(undefined);
  public progressBarValue = input<number | undefined | null>(undefined);

  // Type-specific inputs for progress bar
  public vprScore = input<VprOptions | undefined>(undefined);
  public riskScore = input<Criticality | number | undefined>(undefined);
  public criticality = input<Criticality | undefined | null>(undefined);
  public stackedBarSegments = input<UiStackedProgressBarSegment[] | undefined>(undefined);

  public isNotAvailable = computed(() => {
    const type = this.progressBarType();
    if (!type) {
      return false;
    }

    switch (type) {
      case 'vpr':
        const vpr = this.vprScore();
        return vpr === undefined || vpr === null || vpr === VprOptions.None;
      case 'risk':
        const risk = this.riskScore();
        return risk === undefined || risk === null || risk === Criticality.NoneCriticality;
      case 'criticality':
        const crit = this.criticality();
        return crit === undefined || crit === null || crit === Criticality.NoneCriticality;
      case 'stacked':
        const segments = this.stackedBarSegments();
        return !segments?.length || segments.every((s) => !s || typeof s.value !== 'number' || s.value <= 0);
      default:
        return false;
    }
  });

  public displayValue = computed(() => {
    if (this.isNotAvailable()) {
      return this.languageService.texts.general.not_available;
    }
    return this.value();
  });

  public tooltipValue = computed(() => {
    if (this.isNotAvailable()) {
      return this.languageService.texts.general.not_available;
    }

    if (this.progressBarType() === 'vpr') {
      switch (this.vprScore()) {
        case VprOptions.Low:
          return this.languageService.texts.plugins.vpr_severity.low;
        case VprOptions.Medium:
          return this.languageService.texts.plugins.vpr_severity.medium;
        case VprOptions.High:
          return this.languageService.texts.plugins.vpr_severity.high;
        case VprOptions.Critical:
          return this.languageService.texts.plugins.vpr_severity.critical;
        default:
          return this.languageService.texts.general.not_available;
      }
    }

    return `${this.displayValue()}`;
  });

  protected readonly IconsEnum = IconsEnum;
}

